import { jsxs, jsx } from "react/jsx-runtime";
import { css } from "@/styled-system/css";
import { Box, Text, Heading, Paragraph } from "@zillow/constellation-10";
import { formatLocaleString, currencyFormatter, getYearsAndMonths } from "../../RentVsBuyCalculator.mixins.esm.js";
const graphHighlights = css.raw({
  display: "flex",
  justifyContent: "center"
});
const highlightsBox = css({
  display: "grid",
  alignItems: "center",
  justifyItems: "center",
  padding: {
    base: "token(spacing.250)",
    md: "token(spacing.400)"
  },
  fontSize: {
    base: "token(fontSizes.300)"
  }
});
const rentGainLabel = css({
  color: `{colors.blue.600}`
});
const buyGainLabel = css({
  color: `{colors.green.600}`
});
const headingFontCss = css.raw({
  fontFamily: "heading"
});
const RentVsBuyGraphOutput = (results) => {
  if (!results || !results.graphData) {
    return null;
  }
  const {
    graphData
  } = results;
  const {
    annualCumulativeCosts,
    breakEvenMonths,
    investmentReturnRate,
    purchasePrice,
    yearSelected
  } = graphData;
  let output = null;
  annualCumulativeCosts.map((elem, index) => {
    const buyingCosts = elem.buyingValues.netCosts;
    const rentingCosts = elem.rentingValues.netCosts;
    const {
      savings
    } = elem;
    const buyingEquity = elem.buyingValues.equity;
    const buyingOutOfPocketCosts = elem.buyingValues.totalOutOfPocketCosts;
    const rentingOutOfPocketCosts = elem.rentingValues.totalOutOfPocketCosts;
    const outOfPocketCostsDifference = Math.abs(buyingOutOfPocketCosts - rentingOutOfPocketCosts);
    const opportunityCostsDifference = Math.abs(elem.buyingValues.opportunityCosts - elem.rentingValues.opportunityCosts);
    let bottomLine;
    let outOfPocketCostsComparison;
    let opportunityCostsComparison;
    let currentGainLabel = "BUY";
    if (buyingCosts > rentingCosts) {
      currentGainLabel = "RENT";
    }
    const breakEven = getYearsAndMonths(breakEvenMonths);
    if (breakEvenMonths) {
      bottomLine = /* @__PURE__ */ jsxs("span", { children: [
        /* @__PURE__ */ jsx("strong", { children: "Bottom line:" }),
        " Looking at your gross costs, equity and investment potential, it's better for you to buy than rent if you plan to live in your home more than",
        " ",
        /* @__PURE__ */ jsx("strong", { children: breakEven }),
        "."
      ] });
    } else {
      bottomLine = /* @__PURE__ */ jsxs("span", { children: [
        /* @__PURE__ */ jsx("strong", { children: "Bottom line:" }),
        " Looking at your gross costs, equity and investment potential, you should rent. Buying will never be less expensive than renting."
      ] });
    }
    if (buyingOutOfPocketCosts > rentingOutOfPocketCosts) {
      outOfPocketCostsComparison = /* @__PURE__ */ jsxs("span", { children: [
        "Renting leaves you with",
        " ",
        /* @__PURE__ */ jsx("strong", { children: formatLocaleString(outOfPocketCostsDifference) }),
        " ",
        "in your pocket (including the money you didn't spend on a down payment)."
      ] });
    } else if (buyingOutOfPocketCosts < rentingOutOfPocketCosts) {
      outOfPocketCostsComparison = /* @__PURE__ */ jsxs("span", { children: [
        "Buying leaves you with",
        " ",
        /* @__PURE__ */ jsx("strong", { children: formatLocaleString(outOfPocketCostsDifference) }),
        " ",
        "in your pocket."
      ] });
    } else {
      outOfPocketCostsComparison = /* @__PURE__ */ jsx("span", {});
    }
    if (elem.buyingValues.opportunityCosts > elem.rentingValues.opportunityCosts) {
      opportunityCostsComparison = /* @__PURE__ */ jsxs("span", { children: [
        "However, if you instead rent and invest your down payment and the other money you save, at a",
        " ",
        /* @__PURE__ */ jsxs("strong", { children: [
          investmentReturnRate,
          "%"
        ] }),
        " return rate it will earn around",
        " ",
        /* @__PURE__ */ jsx("strong", { children: formatLocaleString(opportunityCostsDifference) }),
        " ",
        "in ",
        /* @__PURE__ */ jsx("strong", { children: yearSelected }),
        " years."
      ] });
    } else if (elem.buyingValues.opportunityCosts < elem.rentingValues.opportunityCosts) {
      opportunityCostsComparison = /* @__PURE__ */ jsxs("span", { children: [
        "If you also invest the rent you save at",
        " ",
        /* @__PURE__ */ jsxs("strong", { children: [
          investmentReturnRate,
          "%"
        ] }),
        ", in ",
        yearSelected,
        " ",
        "years it will earn around",
        " ",
        /* @__PURE__ */ jsx("strong", { children: formatLocaleString(opportunityCostsDifference) }),
        "."
      ] });
    } else {
      opportunityCostsComparison = /* @__PURE__ */ jsx("span", {});
    }
    if (index === yearSelected - 1) {
      output = /* @__PURE__ */ jsxs(Box, { children: [
        /* @__PURE__ */ jsxs(Box, { css: graphHighlights, children: [
          /* @__PURE__ */ jsxs(Box, { className: highlightsBox, children: [
            /* @__PURE__ */ jsx(Text, { css: headingFontCss, children: "YEAR" }),
            /* @__PURE__ */ jsx(Heading, { level: 4, className: highlightsBox, children: `${yearSelected}` })
          ] }),
          /* @__PURE__ */ jsxs(Box, { className: highlightsBox, children: [
            /* @__PURE__ */ jsx(Text, { css: headingFontCss, children: "BUY" }),
            /* @__PURE__ */ jsx(Heading, { level: 4, className: highlightsBox, children: `$${currencyFormatter(buyingCosts, 1)}` })
          ] }),
          /* @__PURE__ */ jsxs(Box, { className: highlightsBox, children: [
            /* @__PURE__ */ jsx(Text, { css: headingFontCss, children: "RENT" }),
            /* @__PURE__ */ jsx(Heading, { level: 4, className: highlightsBox, children: `$${currencyFormatter(rentingCosts, 1)}` })
          ] }),
          /* @__PURE__ */ jsxs(Box, { className: highlightsBox, children: [
            /* @__PURE__ */ jsxs(Text, { css: headingFontCss, className: currentGainLabel === "BUY" ? buyGainLabel : rentGainLabel, children: [
              currentGainLabel,
              " GAIN"
            ] }),
            /* @__PURE__ */ jsx(Heading, { level: 4, className: `${highlightsBox} ${currentGainLabel === "BUY" ? buyGainLabel : rentGainLabel}`, children: `$${currencyFormatter(savings, 1)}` })
          ] })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "graph-output-explained", children: [
          /* @__PURE__ */ jsxs(Paragraph, { children: [
            /* @__PURE__ */ jsx("strong", { children: "Cost:" }),
            " After",
            " ",
            /* @__PURE__ */ jsx("strong", { children: yearSelected }),
            " years, your total cost of homeownership (down payment, mortgage, taxes, etc.) for a",
            " ",
            /* @__PURE__ */ jsx("strong", { children: formatLocaleString(Number(purchasePrice) || 0) }),
            " ",
            "home would be",
            " ",
            /* @__PURE__ */ jsx("strong", { children: formatLocaleString(buyingOutOfPocketCosts) }),
            ". Your total cost to rent would be",
            " ",
            /* @__PURE__ */ jsx("strong", { children: formatLocaleString(rentingOutOfPocketCosts) }),
            ". ",
            outOfPocketCostsComparison
          ] }),
          /* @__PURE__ */ jsx("br", {}),
          /* @__PURE__ */ jsxs(Paragraph, { children: [
            /* @__PURE__ */ jsx("strong", { children: "Gain:" }),
            " After",
            " ",
            /* @__PURE__ */ jsx("strong", { children: yearSelected }),
            " years, if you buy, your home will have",
            " ",
            /* @__PURE__ */ jsx("strong", { children: formatLocaleString(buyingEquity) }),
            " ",
            "in equity (available to you when you sell).",
            " ",
            opportunityCostsComparison
          ] }),
          /* @__PURE__ */ jsx("br", {}),
          /* @__PURE__ */ jsx(Paragraph, { children: bottomLine })
        ] })
      ] });
    }
    return output;
  });
  return output;
};
export {
  RentVsBuyGraphOutput as default
};
